/**
 * toast-service.ts — Lightweight, framework-agnostic toast notifications.
 *
 * Shared across all EUM web components (and any other client code). Renders
 * into a container appended to <body> so toasts stack above shadow DOMs and
 * modals. No Bootstrap-JS dependency — styling and lifecycle are self-contained.
 *
 * Usage:
 *   import { toastService } from '../core/services/toast-service.js';
 *   toastService.success('Saved');
 *   toastService.error('Save failed', { title: 'Navigation' });
 *   toastService.show({ message: 'Hello', type: 'info', delay: 5000 });
 *
 * Also exposed as `window.eumToast` for non-module callers.
 */

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export type ToastPosition =
    | 'top-right' | 'top-left' | 'top-center'
    | 'bottom-right' | 'bottom-left' | 'bottom-center';

export interface ToastOptions {
    message: string;
    type?: ToastType;
    title?: string;
    /** Auto-dismiss delay in ms. 0 or negative = sticky (manual close only). Default 4000. */
    delay?: number;
    /** Where the toast appears (relative to viewport, or relative to the anchor element if resolved). */
    position?: ToastPosition;
    /**
     * Optional CSS selector or element the toast should position itself against.
     * When the target is found, the toast container is placed *inside* it with
     * `position: absolute` so it sits within that region. If not found (or omitted
     * and no default anchor is set), toasts fall back to fixed viewport positioning.
     */
    anchor?: string | HTMLElement | null;
}

const CONTAINER_ID_PREFIX = 'eum-toast-container';
const STYLE_ID = 'eum-toast-styles';
const DEFAULT_POSITION: ToastPosition = 'top-right';

const POSITIONS: ToastPosition[] = [
    'top-right', 'top-left', 'top-center',
    'bottom-right', 'bottom-left', 'bottom-center',
];

const POSITION_STYLES: Record<ToastPosition, string> = {
    'top-right':     'top: 1rem; right: 1rem; align-items: flex-end;',
    'top-left':      'top: 1rem; left: 1rem; align-items: flex-start;',
    'top-center':    'top: 1rem; left: 50%; transform: translateX(-50%); align-items: center;',
    'bottom-right':  'bottom: 1rem; right: 1rem; align-items: flex-end; flex-direction: column-reverse;',
    'bottom-left':   'bottom: 1rem; left: 1rem; align-items: flex-start; flex-direction: column-reverse;',
    'bottom-center': 'bottom: 1rem; left: 50%; transform: translateX(-50%); align-items: center; flex-direction: column-reverse;',
};

const ICONS: Record<ToastType, string> = {
    success: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16"><path d="M13.485 1.929a.75.75 0 0 1 0 1.06L6.06 10.415a.75.75 0 0 1-1.06 0L2.515 7.929a.75.75 0 1 1 1.06-1.06L5.53 8.823l6.895-6.894a.75.75 0 0 1 1.06 0z"/></svg>',
    error: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16"><path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/><path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"/></svg>',
    warning: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16"><path d="M7.938 2.016A.13.13 0 0 1 8.002 2a.13.13 0 0 1 .063.016.15.15 0 0 1 .054.057l6.857 11.667c.036.06.035.124.002.183a.16.16 0 0 1-.054.06.12.12 0 0 1-.066.017H1.146a.12.12 0 0 1-.066-.017.16.16 0 0 1-.054-.06.18.18 0 0 1 .002-.183L7.884 2.073a.15.15 0 0 1 .054-.057zm1.044-.45a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566z"/><path d="M7.002 12a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 5.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995z"/></svg>',
    info: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16"><path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/><path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z"/></svg>',
};

const STYLE = /*css*/`
.${CONTAINER_ID_PREFIX} {
    position: fixed;
    z-index: 2147483647;
    display: flex;
    flex-direction: column;
    gap: .5rem;
    max-width: min(420px, calc(100vw - 2rem));
    pointer-events: none;
}
.${CONTAINER_ID_PREFIX}.anchored {
    position: absolute;
    z-index: 2147483647;
    max-width: min(420px, calc(100% - 2rem));
}
${POSITIONS.map((p) => `.${CONTAINER_ID_PREFIX}.pos-${p} { ${POSITION_STYLES[p]} }`).join('\n')}
.${CONTAINER_ID_PREFIX} .eum-toast {
    pointer-events: auto;
    display: flex;
    align-items: flex-start;
    gap: .625rem;
    padding: .75rem .875rem;
    border-radius: .5rem;
    background: #fff;
    color: #212529;
    box-shadow: 0 .5rem 1rem rgba(0,0,0,.15), 0 0 0 1px rgba(0,0,0,.05);
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
    font-size: .875rem;
    line-height: 1.35;
    opacity: 0;
    transition: opacity .2s ease, transform .2s ease;
    border-left: 4px solid #6c757d;
    transform: translateY(12px);
}
.${CONTAINER_ID_PREFIX}.pos-top-right    .eum-toast,
.${CONTAINER_ID_PREFIX}.pos-bottom-right .eum-toast { transform: translateX(24px); }
.${CONTAINER_ID_PREFIX}.pos-top-left     .eum-toast,
.${CONTAINER_ID_PREFIX}.pos-bottom-left  .eum-toast { transform: translateX(-24px); }
.${CONTAINER_ID_PREFIX} .eum-toast.show { opacity: 1; transform: translate(0, 0); }
.${CONTAINER_ID_PREFIX}.pos-top-right    .eum-toast.hide,
.${CONTAINER_ID_PREFIX}.pos-bottom-right .eum-toast.hide { opacity: 0; transform: translateX(24px); }
.${CONTAINER_ID_PREFIX}.pos-top-left     .eum-toast.hide,
.${CONTAINER_ID_PREFIX}.pos-bottom-left  .eum-toast.hide { opacity: 0; transform: translateX(-24px); }
.${CONTAINER_ID_PREFIX}.pos-top-center    .eum-toast.hide { opacity: 0; transform: translateY(-12px); }
.${CONTAINER_ID_PREFIX}.pos-bottom-center .eum-toast.hide { opacity: 0; transform: translateY(12px); }
.${CONTAINER_ID_PREFIX} .eum-toast-icon { flex: 0 0 auto; margin-top: 1px; }
.${CONTAINER_ID_PREFIX} .eum-toast-body { flex: 1 1 auto; min-width: 0; word-wrap: break-word; }
.${CONTAINER_ID_PREFIX} .eum-toast-title { font-weight: 600; margin: 0 0 .125rem; }
.${CONTAINER_ID_PREFIX} .eum-toast-close {
    flex: 0 0 auto;
    background: transparent;
    border: 0;
    padding: 0 .125rem;
    font-size: 1.1rem;
    line-height: 1;
    color: inherit;
    opacity: .6;
    cursor: pointer;
}
.${CONTAINER_ID_PREFIX} .eum-toast-close:hover { opacity: 1; }
.${CONTAINER_ID_PREFIX} .eum-toast.type-success { border-left-color: #198754; }
.${CONTAINER_ID_PREFIX} .eum-toast.type-success .eum-toast-icon { color: #198754; }
.${CONTAINER_ID_PREFIX} .eum-toast.type-error   { border-left-color: #dc3545; }
.${CONTAINER_ID_PREFIX} .eum-toast.type-error   .eum-toast-icon { color: #dc3545; }
.${CONTAINER_ID_PREFIX} .eum-toast.type-warning { border-left-color: #ffc107; }
.${CONTAINER_ID_PREFIX} .eum-toast.type-warning .eum-toast-icon { color: #b8860b; }
.${CONTAINER_ID_PREFIX} .eum-toast.type-info    { border-left-color: #0dcaf0; }
.${CONTAINER_ID_PREFIX} .eum-toast.type-info    .eum-toast-icon { color: #0dcaf0; }
`;

export interface IToastService {
    show(opts: ToastOptions | string): () => void;
    success(message: string, opts?: Omit<ToastOptions, 'message' | 'type'>): () => void;
    error(message: string, opts?: Omit<ToastOptions, 'message' | 'type'>): () => void;
    warning(message: string, opts?: Omit<ToastOptions, 'message' | 'type'>): () => void;
    info(message: string, opts?: Omit<ToastOptions, 'message' | 'type'>): () => void;
    /** Change the default position for toasts that don't specify one. */
    setDefaultPosition(position: ToastPosition): void;
    getDefaultPosition(): ToastPosition;
    /**
     * Set a default anchor (CSS selector or element) that all toasts try to
     * position themselves inside. If the anchor can't be resolved at show time,
     * the toast falls back to viewport-fixed positioning.
     */
    setDefaultAnchor(anchor: string | HTMLElement | null): void;
}

class ToastService implements IToastService {
    private containers = new Map<string, HTMLElement>();
    private defaultPosition: ToastPosition = DEFAULT_POSITION;
    private defaultAnchor: string | HTMLElement | null = null;

    setDefaultPosition(position: ToastPosition): void { this.defaultPosition = position; }
    getDefaultPosition(): ToastPosition { return this.defaultPosition; }
    setDefaultAnchor(anchor: string | HTMLElement | null): void { this.defaultAnchor = anchor; }

    /** Show a toast. Returns a dismiss function. */
    show(opts: ToastOptions | string): () => void {
        const options: ToastOptions = typeof opts === 'string' ? { message: opts } : opts;
        const type: ToastType = options.type || 'info';
        const delay = options.delay ?? 4000;
        const position: ToastPosition = options.position || this.defaultPosition;
        const anchorSource = options.anchor !== undefined ? options.anchor : this.defaultAnchor;
        const anchorEl = this.resolveAnchor(anchorSource);

        const container = this.ensureContainer(position, anchorEl);

        const toast = document.createElement('div');
        toast.className = `eum-toast type-${type}`;
        toast.setAttribute('role', type === 'error' ? 'alert' : 'status');
        toast.setAttribute('aria-live', type === 'error' ? 'assertive' : 'polite');

        toast.innerHTML = `
            <span class="eum-toast-icon">${ICONS[type]}</span>
            <div class="eum-toast-body">
                ${options.title ? `<div class="eum-toast-title"></div>` : ''}
                <div class="eum-toast-message"></div>
            </div>
            <button type="button" class="eum-toast-close" aria-label="Close">&times;</button>
        `;

        if (options.title) {
            (toast.querySelector('.eum-toast-title') as HTMLElement).textContent = options.title;
        }
        (toast.querySelector('.eum-toast-message') as HTMLElement).textContent = options.message;

        let hideTimer: number | null = null;
        const dismiss = () => {
            if (hideTimer !== null) { clearTimeout(hideTimer); hideTimer = null; }
            if (!toast.isConnected) return;
            toast.classList.remove('show');
            toast.classList.add('hide');
            setTimeout(() => toast.remove(), 220);
        };

        toast.querySelector('.eum-toast-close')!.addEventListener('click', dismiss);
        container.appendChild(toast);
        requestAnimationFrame(() => toast.classList.add('show'));

        if (delay > 0) hideTimer = window.setTimeout(dismiss, delay);
        return dismiss;
    }

    success(message: string, opts: Omit<ToastOptions, 'message' | 'type'> = {}): () => void {
        return this.show({ ...opts, message, type: 'success' });
    }
    error(message: string, opts: Omit<ToastOptions, 'message' | 'type'> = {}): () => void {
        // Errors are sticky by default — user must dismiss explicitly.
        return this.show({ ...opts, message, type: 'error', delay: 0 });
    }
    warning(message: string, opts: Omit<ToastOptions, 'message' | 'type'> = {}): () => void {
        return this.show({ ...opts, message, type: 'warning' });
    }
    info(message: string, opts: Omit<ToastOptions, 'message' | 'type'> = {}): () => void {
        return this.show({ ...opts, message, type: 'info' });
    }

    private resolveAnchor(source: string | HTMLElement | null | undefined): HTMLElement | null {
        if (!source) return null;
        if (typeof source === 'string') {
            try { return document.querySelector(source) as HTMLElement | null; }
            catch { return null; }
        }
        return source.isConnected ? source : null;
    }

    private ensureContainer(position: ToastPosition, anchor: HTMLElement | null): HTMLElement {
        if (!document.getElementById(STYLE_ID)) {
            const style = document.createElement('style');
            style.id = STYLE_ID;
            style.textContent = STYLE;
            document.head.appendChild(style);
        }

        const parent = anchor || document.body;
        // Ensure anchor establishes a positioning context for absolute children.
        if (anchor) {
            const cs = getComputedStyle(anchor);
            if (cs.position === 'static') anchor.style.position = 'relative';
        }

        // Key containers by both position and target parent so multiple anchors
        // (or anchor + fallback) can coexist without stealing each other's toasts.
        const key = `${position}::${anchor ? this.getAnchorKey(anchor) : 'body'}`;
        let el = this.containers.get(key);
        if (el && el.isConnected && el.parentElement === parent) return el;

        el = document.createElement('div');
        el.className = `${CONTAINER_ID_PREFIX} pos-${position}${anchor ? ' anchored' : ''}`;
        parent.appendChild(el);
        this.containers.set(key, el);
        return el;
    }

    private anchorKeySeq = 0;
    private getAnchorKey(anchor: HTMLElement): string {
        const w = anchor as HTMLElement & { __eumToastKey?: string };
        if (!w.__eumToastKey) w.__eumToastKey = `a${++this.anchorKeySeq}`;
        return w.__eumToastKey;
    }
}

export const toastService: IToastService = new ToastService();

// Portal-wide default: anchor toasts inside the main page region if present,
// otherwise the service transparently falls back to fixed viewport positioning.
toastService.setDefaultAnchor('#EUMPortal .page');

// Expose on window for non-module consumers (legacy scripts, cshtml pages).
try { (window as any).eumToast = toastService; } catch { /* ignore */ }
